import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { from, switchMap, catchError, throwError } from 'rxjs';

let isRefreshing = false;

/** Le chemin seul, sans requete ni parametres : assez pour retrouver la route, sans rien exposer d'autre. */
function cheminDe(url: string): string {
  try {
    return new URL(url, 'http://local').pathname;
  } catch {
    return '(url illisible)';
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);

    // Skip S3 URLs — they use pre-signed auth
    const isS3Url = req.url.includes('.s3.') || req.url.includes('s3.amazonaws.com');
    if (isS3Url) return next(req);

    return from(authService.getToken()).pipe(
        switchMap(token => {
            if (!token) {
                return next(req);
            }

            // Read cached values synchronously from localStorage (fast path)
            // getCompanyId() and getUserName() read from localStorage first before hitting Cognito
            return from(Promise.all([authService.getCompanyId(), authService.getUserName()])).pipe(
                switchMap(([companyId, userName]) => {
                    const headers: Record<string, string> = {
                        Authorization: `Bearer ${token}`
                    };
                    if (companyId) headers['x-company-id'] = companyId;
                    if (userName) headers['x-user-name'] = encodeURIComponent(userName);

                    return next(req.clone({ setHeaders: headers })).pipe(
                        catchError((error: HttpErrorResponse) => {
                            if (error.status === 401 && !isRefreshing) {
                                isRefreshing = true;
                                return from(authService.getToken(true)).pipe(
                                    switchMap(freshToken => {
                                        isRefreshing = false;
                                        if (!freshToken) {
                                            authService.logout(`401 sur ${cheminDe(req.url)} : aucun jeton apres rafraichissement`);
                                            return throwError(() => error);
                                        }
                                        const retryHeaders = { ...headers, Authorization: `Bearer ${freshToken}` };
                                        return next(req.clone({ setHeaders: retryHeaders })).pipe(
                                            catchError((retryError: HttpErrorResponse) => {
                                                if (retryError.status === 401) {
                                                    authService.logout(`401 persistant sur ${cheminDe(req.url)} apres nouvelle tentative`);
                                                }
                                                return throwError(() => retryError);
                                            })
                                        );
                                    }),
                                    catchError(refreshError => {
                                        isRefreshing = false;
                                        return throwError(() => refreshError);
                                    })
                                );
                            }
                            // Reset flag if a 401 arrives while already refreshing
                            if (error.status === 401 && isRefreshing) {
                                isRefreshing = false;
                            }
                            return throwError(() => error);
                        })
                    );
                })
            );
        }),
        catchError((error) => {
            return throwError(() => error);
        })
    );
};
